<script setup>
import {reactive, onMounted, onUnmounted, ref} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {ToastUtil} from "@/Utils/ToastUtil";
import {QuillEditor} from "@vueup/vue-quill";

const compName = 'TimelineAdderComponent';

const props = defineProps({
  contextSchemaA: String,
  contextIdA: String,
  modelValue: {
    type: String,
    default: "",
  }
});

const data = reactive({
  claimStatus: {},
  isDisabled: false,
  isInteraction: false,
  isPrivate: false,
  isPrivateNotes: false,
  tabs: null,
  isTabItemsVisible: false,
  openAdditionalNoteForm: false,
  loading: false,
  dataLoading: false,
  currentFileId: '',
  currentSelection: null,
  note: '',
  additionalInfo: '',
  messageBody: '',
  interactionBody: '',
  contact: { name: '', id: '', type: '' },
  recipients: [],
  interactedWith: { name: '', id: '' },
  people: [],
  contacts: [],
  menuItems: ['Message', 'Note', 'Upload', 'Interaction'],
  newNote: { note: '', additionalInfo: '', isprivate: null },
})

const updateNote = () => {
  data.note = null
}

const updateInteractionBody = () => {
  data.interactionBody = null
}

const updateMessageBody = () => {
  data.messageBody = null
}

const notesRef = ref(null);
const additionalInfoRef = ref(null);

const submit = async (type) => {
  if (type === 'note') {
    data.openAdditionalNoteForm = false;
    data.loading = true
    await ApiWrapper.post('notes', 'create-note', {
          newNote: {
            note: data.note,
            additionalInfo: data.additionalInfo,
            isprivate: data.isPrivateNotes
          },
          claimId: props.contextIdA,
        })
        .then((response) => {
          data.loading = false
          data.isDisabled = false
          activeComponentStore.notifyComponents(['notes'],compName);
          data.note = ''
          data.additionalInfo = ''
          notesRef.value?.setText('');
          additionalInfoRef.value?.setText('');
          data.isPrivateNotes = false

          ToastUtil.successMsg('Created note');
        })
        .catch((e)=>{
          ToastUtil.error(e);
          data.loading = false;
          data.isDisabled = false;
        })

  } else if (type === 'message') {
    data.loading = true
    await ApiWrapper.post('messaging', 'create-message-multiple-recipients', {
          claimId: props.contextIdA,
          message: data.messageBody,
          recipients: data.recipients,
          private: data.isPrivate,
        })
        .then((response) => {
          data.loading = false
          data.isDisabled = false
          activeComponentStore.notifyComponents(['messages','person'],compName);
          data.messageBody = ''
          data.recipients = []
          data.isPrivate = false
          ToastUtil.successMsg('Created message');
        })
        .catch((e)=>{
          ToastUtil.error(e);
          data.loading = false
          data.isDisabled = false
        })
  } else if (type === 'interaction') {
    data.loading = true
    await ApiWrapper.post('relationship-management', 'create-interaction', {
          claimId: props.contextIdA,
          interaction: data.interactionBody,
          contactId: data.contact.id,
          type: data.contact.type,
        })
        .then((response) => {
          data.loading = false
          data.isDisabled = false
          activeComponentStore.notifyComponents(['messages','person'],compName);
          data.interactionBody = ''
          data.contact = { name: '', id: '', type: '' }
          ToastUtil.successMsg('Created interaction');
        })
        .catch((e)=>{
          ToastUtil.error(e);
          data.loading = false
          data.isDisabled = false
        })
  }
  data.tabs = null;
}

const getData = () => {
  data.loading = true;
  ApiWrapper.post('timeline-adder', 'get-people', {
        claimId: props.contextIdA
      })
      .then((response) => {
        if(response.people){
          data.people = response.people;
        }
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.loading = false
      })
  ApiWrapper.post('timeline-adder', 'get-contacts', {
        claimId: props.contextIdA
      })
      .then((response) => {
        if(response.contacts){
          data.contacts = response.contacts;
        }
        activeComponentStore.notifyComponents(['person'],compName);
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.loading = false
      });
  ApiWrapper.post('timeline-adder', 'get-claim-status', {
        claimId: props.contextIdA
      })
      .then((response) => {
          data.claimStatus = {
            isclosed: response.isClosed
          };
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.loading = false
      });
}

const uploadFix = (type) => {}

onMounted(()=>{
  activeComponentStore.registerComponent(
      compName,
      [
        'person'
      ],
      getData
  );

  getData()
})

onUnmounted(() => {
  activeComponentStore.deregisterComponent(compName);
});

</script>

<template>
  <v-container fluid>
    <v-card width="100%">
      <v-toolbar density="compact" flat color="white">
        <v-toolbar-title>Add To Timeline</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        ></v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>
        <div>
          <v-alert
              v-if="data.claimStatus.isclosed === true"
              class="mb-0"
              density="compact"
              variant="outlined"
              type="error"
          >
            Disabled. Claim is closed.
          </v-alert>
        </div>
      </v-toolbar>

      <!-- <v-form ref="form" v-model="valid" lazy-validation> -->
      <v-tabs v-if="data.claimStatus.isclosed === false"  v-model="data.tabs">
        <v-tab class="pa-0 ma-0" style="min-width: 0px"></v-tab>
        <v-tab @click="uploadFix('Note')" value="note"
        ><v-icon class="mr-2">mdi-pencil</v-icon> Note</v-tab
        >
        <!-- <v-tab @click="uploadFix('Upload')"
          ><v-icon class="mr-2">mdi-attachment</v-icon> Upload</v-tab
        > -->
        <v-tab @click="uploadFix('Message')" value="message"
        ><v-icon class="mr-2">mdi-message</v-icon> Message</v-tab
        >
      </v-tabs>
      <v-tabs-window v-if="data.claimStatus.isclosed === false" v-model="data.tabs">
        <v-tabs-window-item
            :transition="false"
            :reverse-transition="false"
        ></v-tabs-window-item>
        <v-tabs-window-item :transition="false" :reverse-transition="false" value="note">
          <v-form>
              <v-card elevation="0">

                <v-card-actions v-if="data.isInteraction">
                  <!-- CHECKBOX FOR CONVERTING NOTE TO INTERACTION - TEMPORARILY DISABLED UNTIL FURTHER NOTICE <v-checkbox
                    class="px-4"
                    label="Was this an interaction?"
                    v-model="isInteraction"
                  ></v-checkbox> -->
                  <v-spacer></v-spacer >
                  <v-menu>

                    <template v-slot:activator="{ on, props }">
                      <v-btn
                          size="small"
                          variant="elevated"
                          v-if="data.contact.name===''"
                          color="primary"
                          dark
                          v-bind="props"
                          v-on="on"
                      >
                        Select Person
                      </v-btn>
                      <v-btn
                          size="small"
                          variant="elevated"
                          v-else
                          color="primary"
                          dark
                          v-bind="props"
                          v-on="on"
                      >
                        {{'Interacted With: ' + data.contact.name}}
                      </v-btn>
                    </template>
                    <v-list density="compact">
                      <v-list-item
                          density="compact"
                          v-for="item in data.contacts"
                          :key="item.name"
                          @click="data.contact=item"
                      >
                        <v-list-item-title
                        >{{ item.name }}</v-list-item-title
                        >
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-card-actions>

                <v-card-actions v-if="data.isInteraction">
                  <v-textarea
                      prepend-inner-icon="mdi-pencil"
                      counter
                      class="pt-2"
                      filled
                      clearable
                      @click:clear="updateInteractionBody()"
                      clear-icon="mdi-close"
                      label="Interaction"
                      hint="Max 70 characters"
                      auto-grow
                      v-model="data.interactionBody"
                      density="compact"
                  >
                  </v-textarea>
                </v-card-actions>

                <div class="pa-2">
                  <QuillEditor theme="snow" v-model:content="data.note" contentType="html" toolbar="essential" ref="notesRef" />
                </div>

                <v-card-actions>
                  <!-- Add Additional Info Button -->
                  <v-btn
                      v-if="!data.openAdditionalNoteForm && data.isInteraction === false && data.note !== null && data.note !== ''"
                      class="grey lighten-2 ml-2"
                      size="x-small"
                      variant="elevated"
                      @click="data.openAdditionalNoteForm = true"
                  >
                    <v-icon left size="small">mdi-plus</v-icon>
                    Add Additional Info (Optional)
                  </v-btn>
                  <v-btn
                      variant="text"
                      v-if="data.openAdditionalNoteForm"
                      class="grey lighten-2 ml-2"
                      size="x-small"
                      @click="data.openAdditionalNoteForm = false; data.additionalInfo=''"
                  >
                    <v-icon left size="small">mdi-window-close</v-icon>
                    Cancel Additional Info
                  </v-btn>
                </v-card-actions>

                <!-- Display Add Additional Info Text Field -->

                <div v-if="data.openAdditionalNoteForm" class="pa-2">
                  <QuillEditor theme="snow" v-model:content="data.additionalInfo" contentType="html" toolbar="essential" ref="additionalInfoRef"/>
                </div>

                <v-card-actions>
                  <v-checkbox
                      density="compact"
                      v-model="data.isPrivateNotes"
                      label="Private?"
                      class="pl-4"
                  ></v-checkbox>
                  <v-spacer></v-spacer>
                  <v-btn
                      size="small"
                      variant="elevated"
                      v-if="data.isInteraction && Permissions.checkPermissions(['relationship-management.create'])"
                      color="primary ml-2"
                      @click="submit('interaction'); data.isDisabled = true"
                      :disabled="data.isDisabled === true || data.interactionBody === '' || data.interactionBody === null || data.contact.name === ''"
                  >Submit</v-btn
                  >
                  <v-btn
                      size="small"
                      variant="elevated"
                      v-if="!data.isInteraction && Permissions.checkPermissions(['notes.create'])"
                      color="primary ml-2"
                      @click="submit('note'); data.isDisabled = true"
                      :disabled="data.isDisabled === true || data.note === '' || data.note === null"
                  >Submit Note</v-btn
                  >
                </v-card-actions>
                <v-divider></v-divider>
              </v-card>
          </v-form>
        </v-tabs-window-item>
        <!-- <v-tab
          :transition="false"
          :reverse-transition="false"
        ></v-tab> -->
        <v-tabs-window-item
            :transition="false"
            :reverse-transition="false"
            value="message"
        >
          <v-card-actions>
            <v-select
                v-model="data.recipients"
                :items="data.people"
                item-title="name"
                item-value="id"
                label="Recipients"
                multiple
                chips
                hint="Select one or more recipients"
                persistent-hint
            >
            </v-select>
          </v-card-actions>
          <v-card-actions class="pb-0">
            <v-textarea
                class="pt-2"
                prepend-inner-icon="mdi-message"
                v-model="data.messageBody"
                clearable
                @click:clear="updateMessageBody()"
                filled
                auto-grow
                label="Enter message"
                hint="Max 70 characters"
                counter
                density="compact"
            ></v-textarea>
          </v-card-actions>
          <v-card-actions class="pt-0">
            <v-checkbox
                density="compact"
                v-model="data.isPrivate"
                label="Private?"
                class="pl-4"
            ></v-checkbox>
            <v-spacer></v-spacer>
            <v-btn
                size="small"
                variant="elevated"
                color="primary"
                class="white--text"
                v-if="Permissions.checkPermissions(['messages.create'])"
                :disabled="data.isDisabled === true || data.messageBody === '' || data.messageBody === null || data.recipients?.name?.length === 0"
                @click="submit('message'); data.isDisabled = true"
            >Submit</v-btn
            >
          </v-card-actions>
        </v-tabs-window-item>
      </v-tabs-window>

      <!-- <div id="drag-drop-area"></div> -->

    </v-card>
  </v-container>
</template>

<style scoped>
#drag-drop-area {
  padding: 5px;
  display: none;
}
.container {
  padding: 5px;
}
[v-cloak] {
  display: none;
}

.appendButton{
  position:sticky;
  bottom:8px;
  width:100%;
  display:flex;
  justify-content :center;
  background :white;
}

.ck.ck-editor {
  width: 100% !important;
}

/* This CSS rule changes color of "browse files" test.  The color is Vuetify "primary". */
.uppy-Dashboard-browse {
  color: #1976d2 !important;
}
</style>